import React, { useEffect, useRef, useState } from 'react';
import useFixedDropdownPosition from './useFixedDropdownPosition';

// Searchable SINGLE-select — the one-value sibling of SearchableMultiSelect
// (same trigger, fixed-positioned menu, search box and outside-click close),
// but rows are plain buttons that pick a value and close the menu, with a
// checkmark on the current pick. Built for the Workspace Agents assign bar,
// where a native <select> over hundreds of stores/workspaces is unusable.

const BASE = {
  trigger: {
    width: '100%', padding: '7px 9px', fontSize: '13px',
    border: '1px solid var(--border,#e2e8f0)', borderRadius: 'var(--radius,6px)',
    background: '#fff', color: 'var(--text-primary,#0f172a)',
  },
};

/**
 * `options` are `{ value, label, hint? }` — `hint` becomes the row's title tooltip.
 * `value` is the selected `option.value` (or '' / null for none); `onChange`
 * receives the picked value and the menu closes. `styles` is the host editor's
 * style object; only `styles.input` is consulted, with a self-contained fallback.
 */
export default function SearchableSelect({
  value,
  options = [],
  onChange,
  styles,
  disabled = false,
  placeholder = '— Select —',
  emptyText = 'No matches',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);
  const menuPos = useFixedDropdownPosition(ref, open, 260);

  useEffect(() => {
    if (!open) { setSearch(''); return undefined; }
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => String(o.label).toLowerCase().includes(q))
    : options;

  const selectedKey = value === null || value === undefined ? '' : String(value);
  const selectedLabel = selectedKey !== ''
    ? (options.find((o) => String(o.value) === selectedKey)?.label ?? selectedKey)
    : '';

  const pick = (v) => {
    if (disabled) return;
    onChange(v);
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        style={{
          ...(styles?.input || BASE.trigger),
          display: 'flex', alignItems: 'center', textAlign: 'left',
          cursor: disabled ? 'default' : 'pointer',
          background: disabled ? '#f1f5f9' : '#fff',
        }}
      >
        <span style={{
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: selectedLabel ? undefined : 'var(--text-muted,#94a3b8)',
        }}>
          {selectedLabel || placeholder}
        </span>
        <span style={{ fontSize: '9px', color: 'var(--text-muted)', flexShrink: 0, marginLeft: '4px' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          ...(menuPos || { position: 'fixed', visibility: 'hidden' }),
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: '1px solid var(--border,#e2e8f0)', borderRadius: 'var(--radius,6px)', background: '#fff',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border,#e2e8f0)', flexShrink: 0 }}>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              style={{
                width: '100%', padding: '5px 7px', fontSize: '12px',
                border: '1px solid var(--border,#e2e8f0)', borderRadius: '4px',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--text-muted)' }}>{emptyText}</div>
            )}
            {filtered.map((o) => {
              const isSelected = String(o.value) === selectedKey;
              return (
                <button
                  key={o.value}
                  type="button"
                  title={o.hint || undefined}
                  onClick={() => pick(o.value)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '7px', width: '100%',
                    padding: '6px 10px', fontSize: '12px', cursor: 'pointer', textAlign: 'left',
                    border: 'none', background: isSelected ? '#f1f5f9' : '#fff',
                    color: 'var(--text-primary,#0f172a)', fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  <span style={{ width: '12px', flexShrink: 0, color: 'var(--primary,var(--cux-accent, #2563eb))' }}>{isSelected ? '✓' : ''}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
